<script>
export default {
  name: "s-petshop-carteira-digital",

  inject: ["hdl"],

  props: ["value"],

  data: () => ({
    loading: false,
    animalInfo: {},
    carteiraDigitalLink: null,
    empresaNome: "",
  }),

  computed: {
    baseUrl() {
      return `${globalThis.location.origin}${Siggma.basePath}`;
    },

    tutor() {
      const pessoa = this.animalInfo.cliente?.pessoa || {};
      return {
        nome: pessoa.nome || "",
        celular: pessoa.celular || "",
        telefone: pessoa.telefone || "",
      };
    },
  },

  watch: {
    value(animalId) {
      if (animalId) {
        this.fetchData(animalId);
      }
    },
  },

  methods: {
    async fetchData(animalId) {
      try {
        this.loading = true;

        const { data } = await this.hdl.http({
          url: `${Siggma.basePath}/pet-shop/animais/get-carteira-digital-pet`,
          data: { animal: animalId },
          loading: false,
        });

        this.setData(data);
      } catch (e) {
        const msg = e.message || "Erro ao carregar dados da carteira digital";
        Notify.error(msg);
      } finally {
        this.loading = false;
      }
    },

    async gerarCarteiraDigital() {
      try {
        this.loading = true;

        await this.hdl.http({
          url: `${Siggma.basePath}/pet-shop/animais/gerar-carteira-digital-pet`,
          data: { animal: this.animalInfo.id },
          loading: false,
        });

        this.fetchData(this.animalInfo.id);
      } catch (e) {
        const msg = e.message || "Erro ao criar carteira digital";
        Notify.error(msg);
      }
    },

    async deleteCarteiraDigital() {
      try {
        this.loading = true;
        const guid = this.animalInfo.extra?.carteiraDigitalGuid;

        if (!guid) {
          Notify.error("GUID da carteira digital não encontrado");
          return;
        }

        await this.hdl.http({
          url:
            `${Siggma.basePath}/pet-shop/animais/delete-carteira-digital-pet`,
          data: { guid },
          loading: false,
        });

        this.fetchData(this.animalInfo.id);
      } catch (e) {
        const msg = e.message || "Erro ao remover carteira digital";
        Notify.error(msg);
      }
    },

    setData(data) {
      this.animalInfo = data.animal || {};
      this.setNomeEmpresa(data.empresa);
      const carteira = data.carteiraDigital;

      if (!carteira) {
        this.carteiraDigitalLink = null;
        return;
      }

      const path = `/publico/carteira-digital?id=${carteira.guid}`;
      this.carteiraDigitalLink = `${this.baseUrl}${path}`;
    },

    setNomeEmpresa(info) {
      if (!info) {
        this.empresaNome = "";
        return;
      }

      this.empresaNome = this.capitalizeWords(
        info?.apelido_fantasia || info?.nome || "",
      );
    },

    gerarLink() {
      Yanda.confirm({
        msg: `
          Deseja gerar um link de compartilhamento para a carteira digital deste animal?
        `,
        actionTrue: () => this.gerarCarteiraDigital(),
      });
    },

    removeLink() {
      Yanda.confirm({
        msg: `
          Deseja remover o link de compartilhamento da carteira digital? Esta ação não pode ser desfeita.
        `,
        actionTrue: () => this.deleteCarteiraDigital(),
      });
    },

    copiarLink() {
      if (this.carteiraDigitalLink) {
        globalThis.navigator.clipboard.writeText(this.carteiraDigitalLink).then(
          () => {
            Notify.success("Link copiado para a área de transferência");
          },
        ).catch(() => {
          Notify.error("Falha ao copiar o link");
        });
      }
    },

    abrirCarteiraDigital() {
      if (this.carteiraDigitalLink) {
        globalThis.open(this.carteiraDigitalLink, "_blank");
      }
    },

    compartilharWhatsapp() {
      if (this.carteiraDigitalLink) {
        const cel = this.tutor.celular;
        const tel = this.tutor.telefone;
        const numeroValido = this.validarTel(cel) || this.validarTel(tel);

        const artigo = this.animalInfo.sexo === "femea" ? "da" : "do";
        let mensagem = `Olá!\n\n`;

        if (this.empresaNome) {
          mensagem +=
            `Acesse a Carteira Digital ${artigo} *${this.animalInfo.nome}* no *ZettaPet* - um serviço da *${this.empresaNome}*:\n\n`;
        } else {
          mensagem +=
            `Acesse a Carteira Digital ${artigo} *${this.animalInfo.nome}* no *ZettaPet*:\n\n`;
        }

        mensagem += `*${this.carteiraDigitalLink}*`;

        if (numeroValido) {
          // Envia para o número validado
          const url = `https://wa.me/55${numeroValido}?text=${
            encodeURIComponent(mensagem)
          }`;
          globalThis.open(url, "_blank");
        } else {
          // Abre WhatsApp sem número (usuário escolhe o contato)
          const url = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
          globalThis.open(url, "_blank");
        }
      }
    },

    validarTel(val) {
      // Remove caracteres não numéricos
      const numeroLimpo = val?.replace(/\D/g, "") || "";
      // Remove zeros à esquerda
      const numeroSemZero = numeroLimpo.replace(/^0+/, "");
      // Valida: DDD (2 dígitos) + Celular (9 dígitos) ou Fixo (8 dígitos)
      const regex = /^(\d{2})(9\d{8}|\d{8})$/;
      const valido = regex.test(numeroSemZero);

      return valido ? numeroSemZero : null;
    },

    capitalizeWords(text) {
      if (!text) return "";
      return text.toLowerCase().split(" ").map((word) =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(" ");
    },

    handleClose() {
      this.animalInfo = {};
      this.carteiraDigitalLink = null;
      this.$emit("close");
    },
  },
};
</script>

<template>
  <s-janela :dad="hdl" :visible="!!value" title="Compartilhar Carteira Digital"
    :width="650" :height="450" @close="handleClose">
    <div id="body" class="s-card card">
      <!-- Loading State -->
      <div v-if="loading" class="content loading-container">
        <div class="spinner-wrapper">
          <div class="spinner"></div>
          <p>Carregando...</p>
        </div>
      </div>

      <!-- Content -->
      <div v-else class="content">
        <div class="info-box">
          <i class="mdi mdi-information-outline mdi-24px"></i>
          <p>
            Compartilhe a carteira digital do animal selecionado através de um
            link exclusivo.
          </p>
        </div>

        <!-- Botão para gerar link quando não existe -->
        <div v-if="!carteiraDigitalLink" class="action-container">
          <button type="button" class="btn-primary btn btn-generate"
            @click="gerarLink">
            <i class="mdi mdi-link-variant mdi-18px"></i>
            Gerar Link de Compartilhamento
          </button>
        </div>

        <!-- Link gerado com opções -->
        <div v-if="carteiraDigitalLink" class="link-container">
          <label class="link-label">Link gerado:</label>
          <div class="link-input-group">
            <input type="text" readonly :value="carteiraDigitalLink"
              class="link-input">
            <div class="copy-icon" @click="copiarLink" title="Copiar link">
              <i class="mdi mdi-content-copy mdi-18px"></i>
            </div>
          </div>

          <div class="action-buttons">
            <button type="button" class="btn-primary btn btn-open"
              @click="abrirCarteiraDigital" title="Abrir carteira digital">
              <i class="mdi mdi-open-in-new mdi-18px"></i>
              Visualizar
            </button>
            <button type="button" class="btn-success btn btn-whatsapp"
              @click="compartilharWhatsapp" title="Compartilhar via WhatsApp">
              <i class="mdi mdi-whatsapp mdi-18px"></i>
              WhatsApp
            </button>
            <button type="button" class="btn-danger btn btn-remove"
              @click="removeLink" title="Remover link">
              <i class="mdi mdi-delete mdi-18px"></i>
              Remover Link
            </button>
          </div>
        </div>
      </div>
    </div>
    <div id="footer">
      <button type="button" style="float: right;" @click="handleClose">
        Fechar
      </button>
    </div>
  </s-janela>
</template>

<style scoped>
.card {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0%;
  margin-left: 2px;
  margin-right: 2px;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.spinner-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2196F3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.spinner-wrapper p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.info-box {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #e3f2fd;
  border-left: 4px solid #2196F3;
  padding: 15px;
  border-radius: 4px;
}

.info-box i {
  color: #2196F3;
  flex-shrink: 0;
}

.info-box p {
  margin: 0;
  color: #1976D2;
  line-height: 1.5;
}

.action-container {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
}

.btn-generate {
  padding: 12px 24px;
  font-size: 15px;
}

.link-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 6px;
}

.link-label {
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
  font-size: 14px;
}

.link-input-group {
  display: flex;
  gap: 8px;
}

.link-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  background-color: white;
  font-family: monospace;
}

.copy-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  cursor: pointer;
  background: transparent;
  border: none;
  outline: none;
  transition: opacity 0.2s ease;
}

.copy-icon i {
  color: #666;
}

.copy-icon:hover i {
  color: #333;
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 10px;
  flex-wrap: wrap;
}

.btn-open {
  padding: 10px 20px;
  flex: 1;
  min-width: 150px;
}

.btn-whatsapp {
  padding: 10px 20px;
  flex: 1;
  min-width: 150px;
}

.btn-remove {
  padding: 10px 20px;
  background-color: #f44336;
  color: white;
  flex: 1;
  min-width: 150px;
}

.btn-remove:hover {
  background-color: #d32f2f;
}

.btn-success {
  background-color: #4CAF50;
  color: white;
}

.btn-success:hover {
  background-color: #45a049;
}

.btn-danger {
  background-color: #f44336;
  color: white;
}
</style>